import axios from 'axios';
import BFFProxy from './bffProxy';
import axiosWithPayloadInterceptor from '../interceptor/axiosWithPayloadInterceptor';
import {
  HTTP_METHODS,
  HTTP_STATUS,
  URLS,
  FILE_TYPES,
} from '../helpers/constants';
import { S3_FILE_UPLOAD_ERROR } from '../helpers/messageConstant';
import { buildPath } from '../util/stringUtil';
/**
 * Fetch data from APIs related to Cost Maintenance
 *
 * @author ysum5308
 */
class CostMaintenanceProxy {
  static async validateItemsBySupcs(data) {
    const config = {
      url: `${URLS.COST_DATA_URL}/items/validate`,
      params: {
        items: data.items.join(','),
        vendors: data.vendors.join(','),
        houseTypes: data.houseTypes.join(','),
      },
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchCostDataRecords(data) {
    const url = `${URLS.COST_DATA_URL}/cost-import-records`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
      params: {
        vendors: data.vendors.join(','),
        houseTypes: data.houseTypes.join(','),
        items: data.items.join(','),
        sourceIndicators: data.sourceIndicators.join(','),
        categoryManagedIndicators: data.categoryManagedIndicators.join(','),
        houseCodes: data.houses.join(','),
        segmentCodes: data.segmentCodes.join(','),
        generateNewCostRecords:
          data.houses.length > 0 && data.segmentCodes.length === 0,
        generateNewSegmentRecords: data.segmentCodes.length > 0,
      },
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchBusinessCenters(suvcs) {
    const config = {
      url: `${URLS.MASTER_DATA_URL}/business-centers`,
      params: {
        suvcs: suvcs.join(','),
      },
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchPriceRecordTypes(data) {
    const config = {
      url: `${URLS.MASTER_DATA_URL}/price-record-types`,
      params: {
        recordType: data.recordType,
        vendors: data.vendors.join(','),
        businessCenters: data.businessCenters.join(','),
      },
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async submitVendorPriceImportFiles(data) {
    const promises = [];
    const { houseType, costChangeDrivers } = data;
    data.submittedFiles.forEach((file) => {
      promises.push(
        BFFProxy.importMultipleAdhocCostFiles({
          file,
          houseType,
          costChangeDriver: costChangeDrivers[file.name],
        }),
      );
    });
    return Promise.all(promises);
  }

  static async importAdhocCostFile(params) {
    const { file, houseType } = params;
    try {
      const signedUrlResponse = await BFFProxy.getSignedUrlToUpload(
        FILE_TYPES.ADHOC_COST_FILE,
        file.name,
      );
      const s3UploadResponse = await BFFProxy.uploadWithSignedUrl(
        signedUrlResponse.data.url,
        file,
        file.type,
      );
      if (s3UploadResponse.status === HTTP_STATUS.OK) {
        const config = {
          url: `${URLS.COST_DATA_URL}/import/adhoc-cost-file`,
          params: {
            houseType,
            filename: encodeURIComponent(signedUrlResponse.data.fileName),
          },
          method: HTTP_METHODS.GET,
        };
        const response = await axiosWithPayloadInterceptor(config);
        return {
          data: response.data,
          fileName: signedUrlResponse.data.fileName,
        };
      }
      return Promise.reject(new Error(S3_FILE_UPLOAD_ERROR));
    } catch (e) {
      return Promise.reject(new Error(e.message));
    }
  }

  static async submitGenerateCostReports(data) {
    const config = {
      url: `${URLS.COST_DATA_URL}/calculate`,
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async fetchFileTransferDetails(houseType, userGroups) {
    const spanDays = 10;
    const config = {
      url: `${URLS.FILE_TRANSFER_DETAILS_URL}`,
      params: {
        house_type: houseType,
        span: spanDays,
        groups: userGroups.join(','),
      },
      method: HTTP_METHODS.GET,
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async importModulePrice(params) {
    const { file } = params;
    try {
      const signedUrlResponse = await BFFProxy.getSignedUrlToUpload(
        FILE_TYPES.MODULE_PRICE_IMPORT_REQUEST,
        file.name,
      );
      const s3UploadResponse = await BFFProxy.uploadWithSignedUrl(
        signedUrlResponse.data.url,
        file,
        file.type,
      );

      if (s3UploadResponse.status === HTTP_STATUS.OK) {
        const data = {
          filePath: `${signedUrlResponse.data.path}/${signedUrlResponse.data.fileName}`,
          fileName: signedUrlResponse.data.fileName,
          actualFileName: file.name,
        };

        const config = {
          url: `${URLS.MODULE_PRICE_IMPORT_URL}`,
          method: HTTP_METHODS.POST,
          data,
        };
        const response = await axiosWithPayloadInterceptor(config);
        return response.data;
      }
      return Promise.reject(new Error(S3_FILE_UPLOAD_ERROR));
    } catch (e) {
      return Promise.reject(new Error(e.message));
    }
  }

  static async fetchAllModules() {
    const url = `${URLS.MODULES_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async exportModulePrice(data) {
    const config = {
      url: `${URLS.MODULE_PRICE_EXPORT_URL}`,
      method: HTTP_METHODS.GET,
      params: {
        moduleIds: data.moduleIds.join(','),
      },
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async getItemPrice(data) {
    const config = {
      url: `${URLS.ITEM_PRICE_URL}`,
      method: HTTP_METHODS.GET,
      params: {
        moduleIds: data.moduleIds.join(','),
      },
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async createModuleGroup(data) {
    const config = {
      url: `${URLS.MODULE_GROUP_CREATE}`,
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAllCommodities() {
    const url = `${URLS.COMMODITIES_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async createCommodityGroup(data) {
    const config = {
      url: `${URLS.COMMODITY_GROUP_CREATE}`,
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchModuleGroupsAssociations(data) {
    const config = {
      url: `${URLS.MODULES_ASSOCIATIONS}`,
      method: HTTP_METHODS.GET,
      params: {
        moduleIds: data.moduleIds.join(','),
      },
    };
    const response = await axios(config);
    return response.data;
  }

  static async deleteModuleGroup(moduleId) {
    const config = {
      url: buildPath(URLS.DELETE_MODULE_GROUP, { moduleId }),
      method: HTTP_METHODS.DELETE,
    };
    const response = await axios(config);
    return response.data;
  }

  static async updateModuleGroup(moduleId, data) {
    const config = {
      url: buildPath(URLS.UPDATE_MODULE_GROUP, { moduleId }),
      method: HTTP_METHODS.PATCH,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async moduleGroupCommodityAssignment(moduleId, data) {
    const config = {
      url: buildPath(URLS.MODULE_GROUP_COMMODITY_ASSIGNMENT, { moduleId }),
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async moduleGroupLoadingAreaAssignment(moduleId, data) {
    const config = {
      url: buildPath(URLS.MODULE_GROUP_LOADING_AREA_ASSIGNMENT, { moduleId }),
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAllLoadingAreas() {
    const url = `${URLS.LOADING_ARES_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async requestCommodityDetails(data) {
    const url = `${URLS.FECTCH_COMMODITY_GROUP}/${data.comcode_id}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async deleteCommodityGroup(comcodeId) {
    const config = {
      url: buildPath(URLS.DELETE_COMMODITY_GROUP, { comcodeId }),
      method: HTTP_METHODS.DELETE,
    };
    const response = await axios(config);
    return response.data;
  }

  static async updateCommodityGroup(comcodeId, data) {
    const config = {
      url: buildPath(URLS.UPDATE_COMMODITY_GROUP, { comcodeId }),
      method: HTTP_METHODS.PATCH,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAllPriceModuleItems() {
    const url = `${URLS.PRICE_MODULE_ITEMS_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async commodityGroupItemAssignment(comcodeId, data) {
    const config = {
      url: buildPath(URLS.COMMODITY_GROUP_ITEM_ASSIGNMENT, { comcodeId }),
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async reactivateModuleGroup(moduleId) {
    const config = {
      url: buildPath(URLS.REACTIVATE_MODULE_GROUP, { moduleId }),
      method: HTTP_METHODS.PUT,
    };
    const response = await axios(config);
    return response.data;
  }

  static async reactivateCommodityGroup(commodityId) {
    const config = {
      url: buildPath(URLS.REACTIVATE_COMMODITY_GROUP, { commodityId }),
      method: HTTP_METHODS.PUT,
    };
    const response = await axios(config);
    return response.data;
  }

  static async getLoadingAreaDetails(request) {
    const config = {
      url: buildPath(URLS.FETCH_LOADING_AREA_DETAILS, {
        loadingAreaId: request.loading_area_id,
      }),
      method: HTTP_METHODS.GET,
    };
    const response = await axios(config);
    return response.data;
  }

  static async createLoadingArea(data) {
    const config = {
      url: `${URLS.CREATE_LOADING_AREA}`,
      method: HTTP_METHODS.POST,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async deleteLoadingArea(loadingAreaId) {
    const config = {
      url: buildPath(URLS.DELETE_LOADING_AREA, { loadingAreaId }),
      method: HTTP_METHODS.DELETE,
    };
    const response = await axios(config);
    return response.data;
  }

  static async updateLoadingArea(loadingAreaId, data) {
    const config = {
      url: buildPath(URLS.UPDATE_LOADING_AREA, { loadingAreaId }),
      method: HTTP_METHODS.PATCH,
      data,
    };
    const response = await axios(config);
    return response.data;
  }

  static async reactivateLoadingArea(loadingAreaId) {
    const config = {
      url: buildPath(URLS.REACTIVATE_LOADING_AREA, { loadingAreaId }),
      method: HTTP_METHODS.PUT,
    };
    const response = await axios(config);
    return response.data;
  }

  static async updateModulePrices(data) {
    const config = {
      url: `${URLS.UPDATE_MODULE_PRICES}`,
      method: HTTP_METHODS.PATCH,
      data,
    };
    const response = await axios(config);
    return response.data;
  }
}

export default CostMaintenanceProxy;
